import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// Hooks
import useDialog from 'hooks/useDialog';
// Actions
import { PropertyOwnershipsActions } from 'store/propertyOwnerships/propertyOwnershipsSlice';
// Models
import IPropertyOwnership from 'models/PropertyOwnership';
import { IDestinationTypeGroups } from 'models/PropertyOwnershipsGroup';
// Components
import ReservationRequestForm from 'components/ReservationRequest.form';
// mui
import { makeStyles } from '@mui/styles';
import {
  Typography, Chip, Box, IconButton,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
// utilities
import { formatStartEndDate } from 'utilities/DateTimeFormatter';

type Props = {
  destinationTypeGroup: IDestinationTypeGroups,
}

const DestinationTypeCard:React.FC<Props> = ({ destinationTypeGroup }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const imgUrl = destinationTypeGroup.destinationType?.image ? destinationTypeGroup.destinationType?.image.url : '/img/noPropertyFound.png';

  const showTimeshares = () => {
    dispatch(PropertyOwnershipsActions.setParams({ destinationTypesIds: [`${destinationTypeGroup.destinationType.id}`] }));
    navigate('timeshares');
  }

  const { Dialog, openDialog, closeDialog } = useDialog();
  
  return (
    <Box className={classes.card}>
      <Box sx={{ height: '178px' }} onClick={showTimeshares}>
        <div className={classes.gradient} />
        <img src={imgUrl} alt={destinationTypeGroup.destinationType.name} className={classes.img} />
        <Typography className={classes.title}>{destinationTypeGroup.destinationType.name}</Typography>
      </Box>
      {destinationTypeGroup.ownerships && (
        destinationTypeGroup.ownerships.slice(0,3).map((ownership: IPropertyOwnership) => (
          <React.Fragment key={ownership.id}>
            <Dialog maxWidth="md">
              <ReservationRequestForm onClose={closeDialog} />
            </Dialog>
            <Box
              onClick={() => {
                navigate(`timeshares/${ownership.id}`);
              }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                mt: 1, p: 1, backgroundColor: 'rgba(232, 236, 237, 1)', borderRadius: '8px', cursor: 'pointer',
              }}
            >
              <Box>
                <Typography className={classes.subtitle} gutterBottom>{ownership.property.name}</Typography>
                <Chip
                  color={ownership.start ? 'default' : 'success'} size="small"
                  icon={<EventAvailableOutlinedIcon />}
                  label={ownership.start ? formatStartEndDate(ownership.start, ownership.end) : 'Flexible dates'}
                  sx={{ backgroundColor: ownership.start ? '#fff' : "auto" }}
                />
              </Box>
              <IconButton
                color="primary"
                onClick={(event: any) => {
                  event.stopPropagation();
                  dispatch(PropertyOwnershipsActions.setPropertyOwnershipDetails(ownership));
                  openDialog();
                }}
              >
                <RequestQuoteOutlinedIcon />
              </IconButton>
            </Box>
          </React.Fragment>
        ))
      )}
    </Box>
  );
}

export default DestinationTypeCard;

const useStyles = makeStyles({
  card: {
    width: '100%',
    cursor: 'pointer',
    position: 'relative',
    '@media (min-width: 1900px)': {
      width: '267px',
    }
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  title: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '160%',
    letterSpacing: '-0.35px',
    color: '#fff',
    position: 'absolute',
    top: 8,
    left: 8,
    textShadow: '0 0 2px #000',
  },
  subtitle: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '157%',
    letterSpacing: '-0.4px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '186px',
  },
  gradient: {
    position: 'absolute',
    width: '100%',
    height: '100px',
    borderRadius: '8px 8px 0 0',
    left: 0,
    top: 0,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)',
  },
});
